import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiRepository } from '../api-repository';
import { Project, ProjectMember, ProjectPage, ProjectSourceValue } from '@core/models/project.model';

/** Query parameters accepted by GET /api/projects/mine/paged. */
export interface MyProjectsQuery {
  search?: string;
  source?: ProjectSourceValue;
  page?: number;
  pageSize?: number;
  includeRequestable?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProjectService extends ApiRepository<Project> {
  override basePath = '/projects';

  /** Projects the current user can enter — all for admins, their memberships otherwise. */
  getMine(): Observable<Project[]> {
    return this.api.get<Project[]>(`${this.basePath}/mine`);
  }

  /**
   * A single page of the same set as {@link getMine}. Used by the project selector so a catalog of
   * several thousand synchronized projects is never fetched or rendered in one go.
   */
  getMinePaged(query: MyProjectsQuery = {}): Observable<ProjectPage> {
    const params = new URLSearchParams();
    if (query.search) params.set('search', query.search);
    if (query.source) params.set('source', query.source);
    params.set('page', String(query.page ?? 1));
    params.set('pageSize', String(query.pageSize ?? 24));
    if (query.includeRequestable) params.set('includeRequestable', 'true');
    return this.api.get<ProjectPage>(`${this.basePath}/mine/paged?${params.toString()}`);
  }

  requestAccess(projectId: string): Observable<void> {
    return this.api.post<void>(`${this.basePath}/${projectId}/access-requests`, {});
  }

  getMembers(projectId: string): Observable<ProjectMember[]> {
    return this.api.get<ProjectMember[]>(`${this.basePath}/${projectId}/members`);
  }

  /** The current user's role in the given project: 'Owner' | 'Member' | 'Viewer' | 'Admin' | ''. */
  getMyRole(projectId: string): Observable<string> {
    return this.api
      .get<{ role: string }>(`${this.basePath}/${projectId}/my-role`)
      .pipe(map(r => r?.role ?? ''));
  }

  addMember(projectId: string, member: Partial<ProjectMember>): Observable<ProjectMember> {
    return this.api.post<ProjectMember>(`${this.basePath}/${projectId}/members`, member);
  }

  removeMember(projectId: string, memberId: string): Observable<void> {
    return this.api.delete<void>(`${this.basePath}/${projectId}/members/${memberId}`);
  }

  /** Distinct tag strings across all non-archived projects (for the "View all" dropdown). */
  getTags(): Observable<string[]> {
    return this.api.get<string[]>(`${this.basePath}/tags`);
  }
}
